import { cn } from "lib/utils";
import type { FC, ReactNode } from "react";

export type TabButtonProps = {
  /** 選択されているか（下線と文字色がこれで決まる） */
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  /**
   * 押した状態を持つトグルとして扱うときに渡す。
   *
   * 見た目は同じでも役割は 2 種類ある。
   *  - 「いま見ている 1 つ」を選ぶタブ（絞り込み・並び替え）: 何も渡さない
   *  - 独立に on/off できる系列トグル: `aria-pressed` に選択状態を渡す
   * ここで自動的に付けてしまうと前者に誤った意味が付くため、呼び出し側の判断にする。
   */
  "aria-pressed"?: boolean;
};

/**
 * 下線型タブの 1 つ（選択中だけ文字を primary にし、下端に 2px の下線を敷く）。
 *
 * 反応の内訳の系列トグル・宛先一覧の絞り込みタブ・直近のやりとりの切り替えが
 * 同じ見た目を別々に持っていたため 1 か所に寄せた。`components/ui/Tabs` とは
 * 寸法（`text-xs` / `pb-1` / 下線 `h-0.5`）が違うので、そちらには寄せない。
 * @param {TabButtonProps} props 選択状態・ハンドラ・ラベル・`aria-pressed`
 * @return {JSX.Element} タブ
 */
export const TabButton: FC<TabButtonProps> = ({
  active,
  onClick,
  children,
  "aria-pressed": ariaPressed,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={ariaPressed}
    className={cn(
      "relative px-1 pb-1 text-xs font-medium transition-colors",
      active ? "text-primary" : "text-base-content/60 hover:text-base-content",
    )}
  >
    {children}
    {active && (
      <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-primary" />
    )}
  </button>
);
